import { addHours, formatISO, isValid, parse, parseISO } from "date-fns";
import { formatInTimeZone, toZonedTime } from "date-fns-tz";
import { z } from "zod";

import { WorkspaceTimezone } from "@common/workspaceTimezone";

export interface DateRange {
    start: Date;
    end: Date;
}

// !!! Should be used in reports only
export const yearOnlyFormat = "yyyy";
export const quarterFullYearFormat = "'Q'Q yyyy";
export const fullMonthFullYearFormat = "MMM yyyy";
export const shortMonthFormat = "MMM";

export const fullDateFullYearFormat = "MMM d, yyyy";
export const receiptRenameDateFormat = "MM.dd.yy";
export const fullMonthDateFullYearFormat = "MMMM d yyyy";
export const fullDateLongFormat = "MM/dd/yyyy";
export const fullDateFormatWithTime = "MMM d, yyyy, h:mm a";
export const timeFormat = "h:mm a";

export function checkDateFormat(dateString: string, format: string) {
    try {
        const date = parse(dateString, format, new Date());

        return isValid(date);
    } catch {
        return false;
    }
}

export const PST_HOURS_OFFSET = 8;

export function getPSTDate(reference = new Date()) {
    return toZonedTime(reference, "PST");
}

// given Date without timezone, this function will assume it's a PST Date
// and will return UTC Date for provided PST datetime
export function getUTCDateForPST(reference = new Date()) {
    return addHours(reference, PST_HOURS_OFFSET);
}

const DATE_FORMATS_TO_CHECK = [
    "MM/dd/yy",
    "MM/dd/yyyy",
    "yyyy-MM-dd",
    "MMM d, ''yy",
    "MMM d, yyyy",
];

export function extractDateFromUnknownFormat(text: string): Date | null {
    for (const format of DATE_FORMATS_TO_CHECK) {
        const date = parse(text, format, new Date());
        if (isValid(date)) {
            return date;
        }
    }
    return null;
}

export function toIsoDate(date: Date): string {
    return formatISO(date, { representation: "date" });
}

// A date-only string (YYYY-MM-DD) denotes a calendar day, not an instant.
// `parseISO` alone resolves it to *local* midnight, which lands on the previous
// calendar day for any runtime east of UTC; anchoring at UTC midnight keeps the
// day stable wherever the code runs and matches how `utcDatetime` (and the
// timestamptz columns fed by it) interpret bare dates.
export function parseDateOnlyAsUTC(dateOnly: string): Date {
    return parseISO(`${dateOnly}T00:00:00Z`);
}

// Inverse of `parseDateOnlyAsUTC`: the calendar day an instant falls on when
// read in UTC. Needed to compare a timestamptz value against a date-only one.
// Accepts a string because TypeORM hands back `date` columns (e.g.
// `invoice.uncollectableAt`) as `YYYY-MM-DD`, not as a `Date`; such a value is
// anchored with `parseDateOnlyAsUTC` rather than `parseISO` so the day does not
// shift on a runtime east of UTC.
export function toUTCDateOnly(date: Date | string): string {
    let instant: Date;
    if (typeof date !== "string") {
        instant = date;
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        instant = parseDateOnlyAsUTC(date);
    } else {
        instant = parseISO(date);
    }
    return formatInTimeZone(instant, "UTC", "yyyy-MM-dd");
}

/** The calendar day (YYYY-MM-DD) an instant falls on in an IANA zone, UTC without one. */
export function toZonedDateOnly({
    date,
    timeZone = "UTC",
}: {
    date: Date;
    timeZone?: string;
}): string {
    return formatInTimeZone(date, timeZone, "yyyy-MM-dd");
}

// Single source of truth for "date X may not precede date Y" rules (e.g. an
// invoice's due/void/uncollectible date vs its invoice date). Date-only strings
// sort lexicographically exactly as they sort chronologically, so the comparison
// is a plain string comparison once both sides are reduced to a calendar day —
// see `toUTCDateOnly` for reducing an instant.
export function isDateOnlyBefore(
    dateOnly: string,
    referenceDateOnly: string,
): boolean {
    return dateOnly < referenceDateOnly;
}

export function addCalendarDays(dateOnly: string, days: number): string {
    const date = parseDateOnlyAsUTC(dateOnly);
    date.setUTCDate(date.getUTCDate() + days);
    return formatInTimeZone(date, "UTC", "yyyy-MM-dd");
}

export function isCalendarDateLocked(
    date: Date | string,
    lockDate: Date | string | null | undefined,
): boolean {
    if (!lockDate) {
        return false;
    }
    return toUTCDateOnly(date) <= toUTCDateOnly(lockDate);
}

/**
 * Calendar months, chronological and de-duplicated, that a books lock covers
 * for the given journal-entry dates. A lock is a single cut-off date, so the
 * locked set is always a contiguous prefix; this names it for the user rather
 * than reporting a bare "period is locked".
 */
export function getLockedMonths(
    dates: Array<Date | string>,
    lockDate: Date | string | null | undefined,
): string[] {
    const monthKeys = new Set<string>();
    for (const date of dates) {
        if (isCalendarDateLocked(date, lockDate)) {
            monthKeys.add(toUTCDateOnly(date).slice(0, 7));
        }
    }
    return [...monthKeys]
        .sort()
        .map((monthKey) =>
            formatInTimeZone(
                parseDateOnlyAsUTC(`${monthKey}-01`),
                "UTC",
                "MMMM yyyy",
            ),
        );
}

export function withCanonicalDateFilters<
    T extends {
        start?: Date;
        end?: Date;
        startDate?: string;
        endDate?: string;
    },
>(filters: T): T {
    return {
        ...filters,
        startDate:
            filters.startDate ??
            (filters.start
                ? formatISO(filters.start, { representation: "date" })
                : undefined),
        endDate:
            filters.endDate ??
            (filters.end
                ? formatISO(filters.end, { representation: "date" })
                : undefined),
    };
}

interface ToWorkspaceDateParams {
    date: Date;
    timezone: WorkspaceTimezone;
}

export function toWorkspaceDateSafe({
    date,
    timezone,
}: ToWorkspaceDateParams): string | null {
    const zonedDate = toZonedTime(date, timezone);
    if (!isValid(zonedDate)) {
        return null;
    }
    return toIsoDate(zonedDate);
}

export function toWorkspaceDate(params: ToWorkspaceDateParams): string {
    const dateOnly = toWorkspaceDateSafe(params);
    if (dateOnly === null) {
        const date = isValid(params.date)
            ? params.date.toISOString()
            : "an invalid date";
        throw new Error(
            `Can't resolve ${date} in workspace timezone ${params.timezone}`,
        );
    }
    return dateOnly;
}

export function getWorkspaceToday(timezone: WorkspaceTimezone): string {
    return toWorkspaceDate({ date: new Date(), timezone });
}

export function getLatestWorkspaceToday(): string {
    return Object.values(WorkspaceTimezone)
        .map((timezone) => getWorkspaceToday(timezone))
        .reduce((latest, today) => (today > latest ? today : latest));
}

interface ResolveProviderDateOnlyParams {
    value: string;
    timezone: WorkspaceTimezone;
}

export function resolveProviderDateOnly({
    value,
    timezone,
}: ResolveProviderDateOnlyParams): string {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value;
    }
    return toWorkspaceDate({ date: parseISO(value), timezone });
}

interface ResolveIntegrationDateOnlyParams {
    date: Date;
    providerDateOnly?: string | null;
    timezone: WorkspaceTimezone;
}

export function resolveIntegrationDateOnly({
    date,
    providerDateOnly,
    timezone,
}: ResolveIntegrationDateOnlyParams): string | null {
    if (providerDateOnly === null) {
        return toWorkspaceDateSafe({ date, timezone });
    }
    return providerDateOnly ?? toUTCDateOnly(date);
}

export function toDateOnlyOrNull(value: string | null): string | null {
    if (!value) {
        return null;
    }
    const date = parseISO(value);
    if (!isValid(date)) {
        return null;
    }
    return toIsoDate(date);
}

export const utcDatetime = z.preprocess((val) => {
    if (typeof val === "string") {
        const hasTimezone = val.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(val);
        return parseISO(hasTimezone ? val : val + "Z");
    }
    return val;
}, z.date());

export const isoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .refine((value) => isValid(parseISO(value)), {
        message: "Invalid calendar date",
    });

export const getDaySuffix = (day: number): string => {
    if (day >= 11 && day <= 13) {
        return "th";
    }
    switch (day % 10) {
        case 1:
            return "st";
        case 2:
            return "nd";
        case 3:
            return "rd";
        default:
            return "th";
    }
};
